import * as mobx from 'mobx';

import { post } from '../Shared/utils/ajax-client';
import { AjaxUtils } from '../Shared/utils/ajax-utils';
import { ICancellablePromise } from '../Shared/utils/cancellable-promise';
import { IWebGivingAppState } from './webgiving-app-state';
import { WebGivingApiConfig } from './webgiving-generated';

export interface IApiAction<TRequest, TResponse = any> {
	readonly url: (model: TRequest) => string;
	request: TRequest;
	response: TResponse;
}

export type WebGivingActionRequest<TActionKey extends keyof WebGivingApiConfigActions> = WebGivingApiConfigActions[TActionKey]['request'];
export type WebGivingActionResponse<TActionKey extends keyof WebGivingApiConfigActions> = WebGivingApiConfigActions[TActionKey]['response'];

export type WebGivingApiConfigActions = typeof WebGivingApiConfig['actions'];

export type WebGivingDataService = {
	[x in keyof WebGivingApiConfigActions]: (data:{request: WebGivingActionRequest<x>}, getAppState: () => IWebGivingAppState) => ICancellablePromise<WebGivingActionResponse<x>>;
};

const options = {
	timeout: 30000,
	newCSRFToken: '',
};

let instance: WebGivingDataService | null = null;

function createActionHandler<TKey extends keyof WebGivingApiConfigActions>(actionKey: TKey) {
	const actionConfig = WebGivingApiConfig.actions[actionKey] as any;

	return (data:{request: any, queryStringParameters?: { [key: string]: string }, timeout?:number}, getAppState: () => IWebGivingAppState) => {
		const request = mobx.isObservable(data.request) ? mobx.toJS(data.request) : { ...data.request };

		const appState = getAppState();
		const customHeaders = appState.person && !appState.rememberMe ? [{ header: 'X-PP-WebGiving-Identity', value: appState.person.SignedPersonId }] : [];
		customHeaders.push({
			header: 'X-PP-WebGiving-CorrelationId',
			value: appState.correlationId,
		});

		return post(actionConfig.url(request), request, {
			timeout: data.timeout || options.timeout,
			baseUrl: AjaxUtils.resolveBaseUrl(WebGivingApiConfig.defaultBaseUrl),
			antiForgeryToken: options.newCSRFToken,
			customHeaders: customHeaders,
			queryStringParameters: data.queryStringParameters,
		}) as any;
	};
}

function createWebGivingDataService(): WebGivingDataService {
	return (Object.keys(WebGivingApiConfig.actions) as Array<keyof WebGivingApiConfigActions>)
		.reduce<WebGivingDataService>((acc: WebGivingDataService, actionKey: keyof WebGivingApiConfigActions) => {
			acc[actionKey] = createActionHandler(actionKey);
			return acc;
		}, {} as WebGivingDataService);
}

export function getWebGivingDataService() {
	if (instance === null) {
		instance = createWebGivingDataService();
	}

	return instance;
}

export function setNewCRSFoken(token: string) {
	options.newCSRFToken = token;
}

export function mockWebGivingDataService(mockDataService: WebGivingDataService) {
	instance = mockDataService;
}
